import React, { useMemo } from 'react';
import { IconButton, Stack, Tooltip, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { useDashboard, useDashboardPageList } from 'routes/dashboard/Dashboard';
import DashboardPageSelector from './DashboardPageSelector';

interface DashboardPageChangeArrowsProps {
  currentPageId: string,
  handleUpdatePageName?: (name: string) => void;
}

export default function DashboardPageChangeArrows(props: DashboardPageChangeArrowsProps): JSX.Element {
  const { currentPageId } = props;
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();

  const dashboard = useDashboard();
  const dashboardPageListOptions = useDashboardPageList();
  const dashboardPages = dashboardPageListOptions.dashboardPages;

  const previousPageId:string | undefined = useMemo(() => {
    const index = dashboardPages.findIndex((page) => page.id === currentPageId);
    if (index > 0) {
      const prevPage = dashboardPages[index - 1];
      return prevPage.id;
    }
  }, [dashboardPages, currentPageId]);

  const nextPageId:string | undefined = useMemo(() => {
    const index = dashboardPages.findIndex((page) => page.id === currentPageId);
    if (index + 1 < dashboardPages.length) {
      const nextPage = dashboardPages[index + 1];
      return nextPage.id;
    }
  }, [dashboardPages, currentPageId]);

  return (
    <Stack
      direction="row"
      sx={{
        justifyContent: "center",
        displayPrint: 'none'
      }}>
      {currentPageId &&
        <>
          {dashboardPages.length > 1 &&
            <Tooltip title={i18n.t("dashboard.previous-page")}>
              <span>
                <IconButton
                  sx={{
                    border: `1px solid ${!previousPageId ? theme.palette.divider : theme.colors.primary.main}`,
                    borderRadius: `${theme.general.borderRadius}`,
                    background: `${theme.colors.primary.lighter}`,
                    mr: 1,
                    "&:hover": { background: "transparent" }
                  }}
                  disabled={!previousPageId}
                  onClick={() => {
                    if (!previousPageId) { return; }
                    const currentUrl = window.location.pathname;
                    navigate(currentUrl.replace(currentPageId, previousPageId));
                  }}
                >
                  <KeyboardArrowLeft sx={{ color: !previousPageId ? theme.palette.divider : theme.colors.primary.main }} />
                </IconButton>
              </span>
            </Tooltip>
          }
          {dashboard &&
            <DashboardPageSelector
              dashboardId={dashboard?.id}
              activePageId={props.currentPageId}
              handleUpdatePageName = {props.handleUpdatePageName}
            />
          }
          {dashboardPages.length > 1 &&
            <Tooltip title={i18n.t("dashboard.next-page")}>
              <span>
                <IconButton
                  sx={{
                    border: `1px solid ${!nextPageId ? theme.palette.divider : theme.colors.primary.main}`,
                    borderRadius: `${theme.general.borderRadius}`,
                    background: `${theme.colors.primary.lighter}`,
                    ml: 1,
                    "&:hover": { background: "transparent" }
                  }}
                  disabled={!nextPageId}
                  onClick={() => {
                    if (!nextPageId) { return; }
                    const currentUrl = window.location.pathname;
                    navigate(currentUrl.replace(currentPageId, nextPageId));
                  }}
                >
                  <KeyboardArrowRight sx={{ color: !nextPageId ? theme.palette.divider : theme.colors.primary.main }} />
                </IconButton>
              </span>
            </Tooltip>
          }
        </>
      }
    </Stack>
  );
}